import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  input,
  output,
  DestroyRef
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  AvailableLanguageCodesEnum,
  BreakPointsMaxInPx,
  Language
} from '@ui/shared/model';
import { TranslatePipe } from '@ngx-translate/core';
import { ImmomioAnchorComponent } from '@immomio/ui/button';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LangPickerComponent } from '../../molecules/lang-picker/lang-picker.component';
import { SocialsComponent } from './socials/socials.component';

export type FooterLinkAppearance = 'default' | 'button';

export interface FooterLink {
  url: string;
  name: string;
  appearance?: FooterLinkAppearance;
  hideOnMobile?: boolean;
}

/**
 * The shape the app footer configs carry — `url` instead of `link`, which is
 * what this component's template reads. It used to live on the now-removed
 * `app-footer-v2`, which had no consumers left.
 */
export interface FooterLinkV2 {
  url: string;
  name: string;
  appearance?: FooterLinkAppearance;
  hideOnMobile?: boolean;
}

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LangPickerComponent,
    FormsModule,
    SocialsComponent,
    AsyncPipe,
    TranslatePipe,
    ImmomioAnchorComponent
  ]
})
export class FooterComponent implements OnInit, OnDestroy {
  #destroyRef = inject(DestroyRef);
  private observer = inject(BreakpointObserver);

  readonly currentYear = input(new Date().getFullYear());
  readonly links = input<FooterLink[]>([]);
  readonly showLanguagePicker = input(false);
  readonly availableLanguages = input<Language[]>(undefined);
  readonly currentLanguageCode = input<AvailableLanguageCodesEnum>(undefined);
  readonly sideNavMode = input<boolean>(undefined);
  readonly dark = input<boolean>(undefined);

  readonly languageChange = output<AvailableLanguageCodesEnum>();

  private _mobileView: BehaviorSubject<boolean>;

  public get mobileView$() {
    return this._mobileView.asObservable().pipe(distinctUntilChanged());
  }

  public ngOnInit() {
    this._mobileView = new BehaviorSubject<boolean>(null);

    this.observer
      .observe(`(max-width: ${BreakPointsMaxInPx.SM}px)`)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(result => {
        this.setMobileView(result.matches);
      });
  }

  public ngOnDestroy() {
    this._mobileView.complete();
  }

  private setMobileView(mobileView: boolean) {
    this._mobileView.next(mobileView);
  }

  public onLanguageChange(value: AvailableLanguageCodesEnum) {
    this.languageChange.emit(value);
  }
}
